/**
 * Custom Rich Text Editor (CKEditor-like)
 * 
 * Features:
 * - CKEditor-style menu bar and toolbar
 * - Full formatting toolbar (bold, italic, underline, etc.)
 * - Font family, size, color, background color
 * - Alignment, lists, links
 * - Image upload with resize handles
 * - Table insertion and editing
 * - Selection handles for images and elements
 * - Preserves exact HTML structure (no normalization)
 * - Maintains all attributes, styles, and scoped CSS
 * 
 * Fix: Source View no longer clears content on toggle.
 *   Root cause was sourceCodeRef being shared between visual-mode (handleInput)
 *   and source-mode (textarea). Introduced sourceViewContentRef to exclusively
 *   own source-mode textarea content so handleInput never corrupts it.
 */
import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { 
	FormatBold, 
	FormatItalic, 
	FormatUnderlined, 
	FormatStrikethrough,
	FormatAlignLeft,
	FormatAlignCenter,
	FormatAlignRight,
	FormatAlignJustify,
	FormatListBulleted,
	FormatListNumbered,
	Link as LinkIcon,
	Image as ImageIcon,
	TableChart,
	Code,
	Undo,
	Redo,
	Subscript,
	Superscript,
	Close,
	SwapHoriz,
	AddCircleOutline,
	RemoveCircleOutline,
} from '@mui/icons-material';
import { IconButton, Divider, Select, MenuItem, Tooltip, Box, Button, Popover } from '@mui/material';
import { uploadFiles } from '../../utils/common';
import { getToken } from '../../utils/common';
import { postV1Drive } from '../../api-client/api.drive/api';
import { getApiConfig } from '../../utils/api-config';
import { CellProperties } from './table/types';
import { prepareForEditing, prepareForSaving } from './utils/handlebars-protection';
import './custom-editor.css';

export interface CustomEditorProps {
	editorRef?: React.RefObject<any>;
	data: string;
	handleEditorReady: (editor: any) => void;
	onError?: (error: Error) => void;
	config?: any;
	[key: string]: any;
}

const FONT_FAMILIES = [
	'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Trebuchet MS', 'Verdana'
];

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

const HEADING_FORMATS = [
	{ value: 'p', label: 'Paragraph' },
	{ value: 'h1', label: 'Heading 1' },
	{ value: 'h2', label: 'Heading 2' },
	{ value: 'h3', label: 'Heading 3' },
	{ value: 'h4', label: 'Heading 4' },
	{ value: 'h5', label: 'Heading 5' },
	{ value: 'h6', label: 'Heading 6' }
];

const PRESET_COLORS = [
	'#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#ffffff',
	'#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#9900ff', '#ff00ff',
	'#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#9fc5e8', '#b4a7d6', '#d5a6bd',
	'#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6fa8dc', '#8e7cc3', '#c27ba0',
	'#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3d85c8', '#674ea7', '#85200c',
];

export const CustomEditor = forwardRef<any, CustomEditorProps>((props, ref) => {
	const {
		editorRef: editorRefProp,
		data = '',
		handleEditorReady,
		onError,
		config = {},
		...rest
	} = props;

	const [error, setError] = useState<Error | null>(null);
	const [fontFamily, setFontFamily] = useState('Arial');
	const [fontSize, setFontSize] = useState('14px');
	const [headingFormat, setHeadingFormat] = useState('p');
	const [showSourceMode, setShowSourceMode] = useState(false);
	const [sourceCode, setSourceCode] = useState(data);
	const [history, setHistory] = useState<string[]>([data]);
	const [historyIndex, setHistoryIndex] = useState(0);
	const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
	const [resizeHandles, setResizeHandles] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
	const [showTablePicker, setShowTablePicker] = useState(false);
	const [tablePickerAnchor, setTablePickerAnchor] = useState<HTMLElement | null>(null);
	const [textSelectionAnchor, setTextSelectionAnchor] = useState<{ x: number; y: number } | null>(null);
	const [showTextToolbar, setShowTextToolbar] = useState(false);
	const [colorPickerState, setColorPickerState] = useState<{ anchor: HTMLElement; type: 'text' | 'bg' } | null>(null);
	
	// Table editing state
	const [selectedCell, setSelectedCell] = useState<HTMLTableCellElement | null>(null);
	const [showTableToolbar, setShowTableToolbar] = useState(false);
	const [tableToolbarPosition, setTableToolbarPosition] = useState<{ x: number; y: number } | null>(null);
	const [cellProperties, setCellProperties] = useState<CellProperties>({
		borderStyle: 'solid',
		borderColor: '#ddd',
		borderWidth: '1px',
		backgroundColor: 'transparent',
		width: 'auto',
		height: 'auto',
		padding: '8px',
		textAlign: 'left',
		verticalAlign: 'middle'
	});
	
	const contentEditableRef = useRef<HTMLDivElement>(null);
	const sourceCodeRef = useRef<string>(data);
	const onEditorChangeRef = useRef(props.onEditorChange);
	const handleEditorReadyRef = useRef(handleEditorReady);
	useEffect(() => { onEditorChangeRef.current = props.onEditorChange; });
	useEffect(() => { handleEditorReadyRef.current = handleEditorReady; });

	// FIX: Dedicated ref for source-mode textarea content.
	// sourceCodeRef is shared with visual-mode (handleInput overwrites it on every
	// keystroke). sourceViewContentRef is ONLY written by source-mode handlers
	// (handleSourceCodeChange, handleHtmlFileUpload, handleToggleSource entering
	// source mode) so it is never corrupted by visual-mode activity.
	const sourceViewContentRef = useRef<string>(data);

	const fileInputRef = useRef<HTMLInputElement>(null);
	const htmlFileInputRef = useRef<HTMLInputElement>(null);
	const replaceImageInputRef = useRef<HTMLInputElement>(null);
	const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number; handle: string } | null>(null);
	const handlebarsMapRef = useRef<{ [key: string]: string }>({});
	const apiConfig = getApiConfig();

	// Update source code when data prop changes
	useEffect(() => {
		if(!data)return
		const { html: protectedHtml, map } = prepareForEditing(data);
		handlebarsMapRef.current = map;
		sourceCodeRef.current = data; // Store original with Handlebars
		
		if (contentEditableRef.current && contentEditableRef.current.innerHTML !== protectedHtml) {
			contentEditableRef.current.innerHTML = protectedHtml;
		}
	}, [data]);

	// Initialize editor
	useEffect(() => {
		if (contentEditableRef.current) {
			try {
				const { html: protectedHtml, map } = prepareForEditing(data || '');
				handlebarsMapRef.current = map;
				contentEditableRef.current.innerHTML = protectedHtml;
				const adapter = {
					getData: () => {
						// Restore Handlebars syntax before returning
						return prepareForSaving(sourceCodeRef.current, handlebarsMapRef.current);
					},
					setData: (newData: string) => {
						const { html: protectedHtml, map } = prepareForEditing(newData);
						handlebarsMapRef.current = map;
						sourceCodeRef.current = newData;
						if (contentEditableRef.current) {
							contentEditableRef.current.innerHTML = protectedHtml;
						}
					},
					model: {
						insertContent: (fragment: string) => {
							execCommand('insertHTML', fragment);
						}
					},
					data: {
						processor: {
							toView: (html: string) => html
						},
						toModel: (html: string) => html
					}
				};

				handleEditorReadyRef.current?.(adapter);
			} catch (e) {
				console.error('[CustomEditor] init effect error:', e);
			}
		}
	}, []);

	// Execute formatting command
	const execCommand = useCallback((command: string, value?: string) => {
		document.execCommand(command, false, value);
		if (contentEditableRef.current) {
			sourceCodeRef.current = contentEditableRef.current.innerHTML;
			saveToHistory();
		}
	}, []);

	// Save to history for undo/redo
	const saveToHistory = useCallback(() => {
		if (contentEditableRef.current) {
			const newHistory = history.slice(0, historyIndex + 1);
			newHistory.push(contentEditableRef.current.innerHTML);
			setHistory(newHistory.slice(-50)); // Keep last 50 states
			setHistoryIndex(newHistory.length - 1);
		}
	}, [history, historyIndex]);

	// Undo
	const handleUndo = useCallback(() => {
		if (historyIndex > 0) {
			const newIndex = historyIndex - 1;
			setHistoryIndex(newIndex);
			if (contentEditableRef.current) {
				contentEditableRef.current.innerHTML = history[newIndex];
				sourceCodeRef.current = history[newIndex];
			}
		}
	}, [history, historyIndex]);

	// Redo
	const handleRedo = useCallback(() => {
		if (historyIndex < history.length - 1) {
			const newIndex = historyIndex + 1;
			setHistoryIndex(newIndex);
			if (contentEditableRef.current) {
				contentEditableRef.current.innerHTML = history[newIndex];
				sourceCodeRef.current = history[newIndex];
			}
		}
	}, [history, historyIndex]);

	// Handle content changes in visual mode.
	// IMPORTANT: only updates sourceCodeRef (visual-mode ref).
	// Must NOT touch sourceViewContentRef — that ref belongs exclusively to source mode.
	const handleInput = useCallback(() => {
		if (contentEditableRef.current) {
			const visualHtml = contentEditableRef.current.innerHTML;
			// Restore Handlebars before saving to sourceCodeRef
			sourceCodeRef.current = prepareForSaving(visualHtml, handlebarsMapRef.current);
			onEditorChangeRef.current?.(sourceCodeRef.current);
		}
	}, []);

	// Strip external formatting from pasted HTML.
	const sanitizePastedHtml = useCallback((html: string): string => {
		const doc = new DOMParser().parseFromString(html, 'text/html');

		// Unwrap <span> elements — they carry font/color styles from external sources
		doc.querySelectorAll('span').forEach(span => {
			const frag = document.createDocumentFragment();
			while (span.firstChild) frag.appendChild(span.firstChild);
			span.parentNode?.replaceChild(frag, span);
		});

		// Convert <a> links to plain text
		doc.querySelectorAll('a').forEach(a => {
			const text = document.createTextNode(a.textContent || '');
			a.parentNode?.replaceChild(text, a);
		});

		// Strip style and class from every remaining element
		doc.querySelectorAll('[style], [class], [color], [bgcolor], [face]').forEach(el => {
			el.removeAttribute('style');
			el.removeAttribute('class');
			el.removeAttribute('color');
			el.removeAttribute('bgcolor');
			el.removeAttribute('face');
		});

		return doc.body.innerHTML;
	}, []);

	// Handle paste — sanitize external HTML before inserting
	const handlePaste = useCallback((e: React.ClipboardEvent) => {
		e.preventDefault();
		const html = e.clipboardData.getData('text/html');
		const text = e.clipboardData.getData('text/plain');

		if (html) {
			execCommand('insertHTML', sanitizePastedHtml(html));
		} else {
			execCommand('insertText', text);
		}
	}, [execCommand, sanitizePastedHtml]);

	// Handle image upload
	const handleImageUpload = useCallback(async (file: File) => {
		try {
			const uFiles = await uploadFiles([file], 'document');
			const token = getToken();
			const payload = {
				...token,
				'x-timezone': token['x-timezone']?.toString() || '',
				title: file?.name.split('.')[0],
				path: '/',
				is_private: true,
				is_directory: false,
				file_size: file.size,
				is_child: false,
				file_extension: file?.name.split('.')[1],
				type: 'image',
				mime_type: file.type,
				url: `${apiConfig?.s3BucketUrl}${uFiles[0]}`
			};
			
			await postV1Drive(payload);
			execCommand('insertHTML', `<img src="${payload.url}" alt="${file.name}" style="max-width: 100%;" />`);
		} catch (error) {
			console.error('Image upload failed:', error);
			onError?.(error as Error);
		}
	}, [apiConfig, execCommand, onError]);

	// Handle file input change
	const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handleImageUpload(file);
		}
	}, [handleImageUpload]);

	// Insert link
	const handleInsertLink = useCallback(() => {
		const url = prompt('Enter URL:');
		if (url) {
			execCommand('createLink', url);
		}
	}, [execCommand]);

	// Insert table
	const handleInsertTable = useCallback(() => {
		const rows = prompt('Number of rows:', '3');
		const cols = prompt('Number of columns:', '3');
		
		if (rows && cols) {
			let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%;">';
			tableHTML += '<thead><tr>';
			for (let i = 0; i < parseInt(cols); i++) {
				tableHTML += '<th style="border: 1px solid #ddd; padding: 8px;">Header</th>';
			}
			tableHTML += '</tr></thead><tbody>';
			for (let i = 0; i < parseInt(rows); i++) {
				tableHTML += '<tr>';
				for (let j = 0; j < parseInt(cols); j++) {
					tableHTML += '<td style="border: 1px solid #ddd; padding: 8px;"></td>';
				}
				tableHTML += '</tr>';
			}
			tableHTML += '</tbody></table>';
			
			execCommand('insertHTML', tableHTML);
		}
	}, [execCommand]);

	// Table picker component
	const TablePicker = () => {
		const [hoverCell, setHoverCell] = useState<{ row: number; col: number } | null>(null);
		const maxRows = 10;
		const maxCols = 10;
		
		// Calculate position relative to button
		const buttonRect = tablePickerAnchor?.getBoundingClientRect();
		const editorContainer = document.querySelector('.custom-editor-container');
		const containerRect = editorContainer?.getBoundingClientRect();
		
		let top = 0;
		let left = 0;
		
		if (buttonRect && containerRect) {
			top = buttonRect.bottom - containerRect.top + 5;
			left = buttonRect.left - containerRect.left;
		}
		
		return (
			<Box
				sx={{
					position: 'absolute',
					top: `${top}px`,
					left: `${left}px`,
					bgcolor: 'white',
					border: '1px solid #ddd',
					borderRadius: 1,
					p: 2,
					boxShadow: 3,
					zIndex: 9999
				}}
			>
				<Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${maxCols}, 20px)`, gap: '2px', mb: 1 }}>
					{Array.from({ length: maxRows }).map((_, rowIndex) =>
						Array.from({ length: maxCols }).map((_, colIndex) => (
							<Box
								key={`${rowIndex}-${colIndex}`}
								onMouseEnter={() => setHoverCell({ row: rowIndex + 1, col: colIndex + 1 })}
								onClick={() => {
									handleInsertTableFromPicker(rowIndex + 1, colIndex + 1);
									setHoverCell(null);
								}}
								sx={{
									width: 20,
									height: 20,
									border: '1px solid #ddd',
									bgcolor: hoverCell && rowIndex < hoverCell.row && colIndex < hoverCell.col ? '#1976d2' : 'white',
									cursor: 'pointer',
									'&:hover': {
										bgcolor: '#1976d2'
									}
								}}
							/>
						))
					)}
				</Box>
				<Box sx={{ textAlign: 'center', fontSize: '12px', color: '#666' }}>
					{hoverCell ? `${hoverCell.row} × ${hoverCell.col}` : '1 × 1'}
				</Box>
			</Box>
		);
	};

	// Font family change
	const handleFontFamilyChange = useCallback((e: any) => {
		const family = e.target.value;
		setFontFamily(family);
		execCommand('fontName', family);
	}, [execCommand]);

	// Font size change
	const handleFontSizeChange = useCallback((e: any) => {
		const size = e.target.value;
		setFontSize(size);
		document.execCommand('fontSize', false, '7');
		if (contentEditableRef.current) {
			const fontTags = Array.from(
				contentEditableRef.current.querySelectorAll('font[size="7"]')
			);
			fontTags.forEach(tag => {
				const span = document.createElement('span');
				span.style.fontSize = size;
				while (tag.firstChild) span.appendChild(tag.firstChild);
				tag.parentNode?.replaceChild(span, tag);
			});
			sourceCodeRef.current = contentEditableRef.current.innerHTML;
			saveToHistory();
			onEditorChangeRef.current?.(sourceCodeRef.current);
		}
	}, [saveToHistory]);

	// Heading format change
	const handleHeadingFormatChange = useCallback((e: any) => {
		const format = e.target.value;
		setHeadingFormat(format);
		execCommand('formatBlock', `<${format}>`);
	}, [execCommand]);

	// Toggle source mode
	// FIX: Use sourceViewContentRef (not sourceCodeRef) as the single source of
	// truth for the textarea. This prevents handleInput (visual mode) from ever
	// overwriting content the user typed/pasted into the source textarea.
	const handleToggleSource = useCallback(() => {
		if (!showSourceMode) {
			// Entering source mode: MUST capture content BEFORE state change
			// because contentEditableRef.current becomes null after re-render
			const visualHtml = contentEditableRef.current?.innerHTML || sourceCodeRef.current || data;
			const restoredHtml = prepareForSaving(visualHtml, handlebarsMapRef.current);
			
			// Update all refs and state
			setSourceCode(restoredHtml);
			sourceViewContentRef.current = restoredHtml;
			sourceCodeRef.current = restoredHtml;
			
			// Now switch mode (this will unmount contentEditable and mount textarea)
			setShowSourceMode(true);
		} else {
			// Leaving source mode: read from sourceViewContentRef
			// State change will mount contentEditable, then we update it in useEffect
			const currentSource = sourceViewContentRef.current.trim();
			
			if (!currentSource) {
				// Empty source — use default
				const defaultHtml = '<p><br></p>';
				sourceCodeRef.current = defaultHtml;
				sourceViewContentRef.current = defaultHtml;
				setSourceCode(defaultHtml);
			} else {
				sourceCodeRef.current = currentSource;
			}
			
			// Switch mode first
			setShowSourceMode(false);
		}
	}, [showSourceMode, data]);

	// Effect to update contentEditable when switching from source to visual mode
	// Use a ref to track if we've already processed this mode switch
	const hasProcessedModeSwitch = useRef(false);
	
	useEffect(() => {
		// Only run when switching TO visual mode (showSourceMode becomes false)
		// and we haven't already processed this switch
		if (!showSourceMode && contentEditableRef.current && !hasProcessedModeSwitch.current) {
			hasProcessedModeSwitch.current = true;
			
			try {
				const currentSource = sourceCodeRef.current.trim();
				
				if (!currentSource) {
					const defaultHtml = '<p><br></p>';
					const { html: protectedHtml, map } = prepareForEditing(defaultHtml);
					handlebarsMapRef.current = map;
					contentEditableRef.current.innerHTML = protectedHtml;
				} else {
					const { html: protectedHtml, map } = prepareForEditing(currentSource);
					handlebarsMapRef.current = map;

					if (!protectedHtml || protectedHtml.trim() === '') {
						console.warn('Protected HTML is empty, using original source');
						contentEditableRef.current.innerHTML = currentSource;
					} else {
						contentEditableRef.current.innerHTML = protectedHtml;
					}
				}
				
				// Save to history without triggering re-render loop
				const newHistory = history.slice(0, historyIndex + 1);
				newHistory.push(contentEditableRef.current.innerHTML);
				setHistory(newHistory.slice(-50));
				setHistoryIndex(newHistory.length - 1);
			} catch (error) {
				console.error('Error updating visual mode:', error);
				// Fallback: use raw HTML
				if (contentEditableRef.current && sourceCodeRef.current) {
					try {
						contentEditableRef.current.innerHTML = sourceCodeRef.current;
					} catch (innerError) {
						console.error('Failed to render HTML:', innerError);
					}
				}
			}
		}
		
		// Reset the flag when entering source mode
		if (showSourceMode) {
			hasProcessedModeSwitch.current = false;
		}
	}, [showSourceMode, history, historyIndex]);

	// Handle HTML file upload
	// FIX: also write to sourceViewContentRef so the uploaded content is not
	// lost when the user toggles to visual mode.
	const handleHtmlFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		
		reader.onload = (event) => {
			const htmlContent = event.target?.result as string;
			if (htmlContent) {
				// Update both textarea state and the dedicated source-mode ref
				setSourceCode(htmlContent);
				sourceViewContentRef.current = htmlContent; // FIX: was sourceCodeRef.current
				sourceCodeRef.current = htmlContent;
				
				// If in visual mode, update the visual editor immediately
				if (!showSourceMode && contentEditableRef.current) {
					try {
						const { html: protectedHtml, map } = prepareForEditing(htmlContent);
						handlebarsMapRef.current = map;
						contentEditableRef.current.innerHTML = protectedHtml;
						saveToHistory();
					} catch (error) {
						console.error('Error processing uploaded HTML:', error);
						alert('There was an error processing the HTML file. Please check the file and try again.');
					}
				}
			}
		};

		reader.onerror = () => {
			alert('Failed to read HTML file. Please try again.');
		};

		reader.readAsText(file);
		
		// Reset input so the same file can be uploaded again
		e.target.value = '';
	}, [showSourceMode, saveToHistory]);

	// Handle source code textarea change.
	// FIX: write to sourceViewContentRef (not sourceCodeRef) so visual-mode
	// handleInput can never overwrite what the user is typing in the textarea.
	const handleSourceCodeChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newCode = e.target.value;
		setSourceCode(newCode);
		sourceViewContentRef.current = newCode; // FIX: was sourceCodeRef.current
	}, []);

	// Handle click on images/elements for selection
	const handleContentClick = useCallback((e: React.MouseEvent) => {
		const target = e.target as HTMLElement;
		
		// Check if clicked on image or table
		let elementToSelect: HTMLElement | null = null;
		
		if (target.tagName === 'IMG') {
			e.preventDefault();
			e.stopPropagation();
			elementToSelect = target;
		} else if (target.tagName === 'TABLE') {
			e.preventDefault();
			e.stopPropagation();
			elementToSelect = target;
		} else if (target.tagName === 'TD' || target.tagName === 'TH' || target.tagName === 'TR' || target.tagName === 'TBODY' || target.tagName === 'THEAD') {
			// If clicked inside table, find the parent table
			e.preventDefault();
			e.stopPropagation();
			elementToSelect = target.closest('table');
		}
		
		if (elementToSelect) {
			setSelectedElement(elementToSelect);
			
			const rect = elementToSelect.getBoundingClientRect();
			const containerRect = contentEditableRef.current?.getBoundingClientRect();
			
			if (containerRect) {
				setResizeHandles({
					x: rect.left - containerRect.left,
					y: rect.top - containerRect.top,
					width: rect.width,
					height: rect.height
				});
			}
		} else {
			setSelectedElement(null);
			setResizeHandles(null);
		}
	}, []);

	// Delete selected element
	const handleDeleteSelected = useCallback(() => {
		if (selectedElement) {
			selectedElement.remove();
			setSelectedElement(null);
			setResizeHandles(null);
			if (contentEditableRef.current) {
				sourceCodeRef.current = contentEditableRef.current.innerHTML;
				saveToHistory();
			}
		}
	}, [selectedElement, saveToHistory]);

	// Replace image
	const handleReplaceImage = useCallback(async (file: File) => {
		if (!selectedElement || selectedElement.tagName !== 'IMG') return;
		
		try {
			const uFiles = await uploadFiles([file], 'document');
			const token = getToken();
			const payload = {
				...token,
				'x-timezone': token['x-timezone']?.toString() || '',
				title: file?.name.split('.')[0],
				path: '/',
				is_private: true,
				is_directory: false,
				file_size: file.size,
				is_child: false,
				file_extension: file?.name.split('.')[1],
				type: 'image',
				mime_type: file.type,
				url: `${apiConfig?.s3BucketUrl}${uFiles[0]}`
			};
			
			await postV1Drive(payload);
			(selectedElement as HTMLImageElement).src = payload.url;
			
			if (contentEditableRef.current) {
				sourceCodeRef.current = contentEditableRef.current.innerHTML;
				saveToHistory();
			}
		} catch (error) {
			console.error('Image upload failed:', error);
			onError?.(error as Error);
		}
	}, [selectedElement, apiConfig, onError, saveToHistory]);

	// Handle replace image file input
	const handleReplaceImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handleReplaceImage(file);
		}
	}, [handleReplaceImage]);

	// Handle text selection
	const handleTextSelection = useCallback(() => {
		const selection = window.getSelection();
		if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
			const range = selection.getRangeAt(0);
			const rect = range.getBoundingClientRect();
			const containerRect = contentEditableRef.current?.getBoundingClientRect();
			
			if (containerRect && rect.width > 0) {
				setTextSelectionAnchor({
					x: rect.left + rect.width / 2,
					y: rect.top - 10
				});
				setShowTextToolbar(true);
			}

			// Sync font size dropdown to the selected text's actual font size.
			// Walk up from the anchor node looking for an explicit inline font-size.
			let node: Node | null = selection.anchorNode;
			if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
			let detectedSize: string | null = null;
			let current = node instanceof HTMLElement ? node : null;
			while (current && current !== contentEditableRef.current) {
				if (current.style.fontSize) {
					detectedSize = current.style.fontSize;
					break;
				}
				current = current.parentElement;
			}
			setFontSize(detectedSize ?? '14px');
		} else {
			setShowTextToolbar(false);
			setTextSelectionAnchor(null);
		}
	}, []);

	// Handle resize start
	const handleResizeStart = useCallback((e: React.MouseEvent, handle: string) => {
		e.preventDefault();
		e.stopPropagation();
		
		if (selectedElement && resizeHandles) {
			resizeStartRef.current = {
				x: e.clientX,
				y: e.clientY,
				width: resizeHandles.width,
				height: resizeHandles.height,
				handle
			};
			
			// Add event listeners
			document.addEventListener('mousemove', handleResizeMove as any);
			document.addEventListener('mouseup', handleResizeEnd);
		}
	}, [selectedElement, resizeHandles]);

	// Handle resize move
	const handleResizeMove = useCallback((e: MouseEvent) => {
		if (!resizeStartRef.current || !selectedElement || !resizeHandles) return;
		
		const { x, y, width, height, handle } = resizeStartRef.current;
		const deltaX = e.clientX - x;
		const deltaY = e.clientY - y;
		
		let newWidth = width;
		let newHeight = height;
		
		if (handle.includes('e')) {
			newWidth = Math.max(50, width + deltaX);
		}
		if (handle.includes('w')) {
			// West handle: shrink/grow from the left — invert delta
			newWidth = Math.max(50, width - deltaX);
		}
		if (handle.includes('s')) {
			newHeight = Math.max(50, height + deltaY);
		}
		if (handle.includes('n')) {
			newHeight = Math.max(50, height - deltaY);
		}
		
		// Maintain aspect ratio for corner handles on images
		if (handle.length === 2 && selectedElement.tagName === 'IMG') {
			const aspectRatio = width / height;
			newHeight = newWidth / aspectRatio;
		}
		
		selectedElement.style.width = `${newWidth}px`;
		if (selectedElement.tagName === 'IMG' || selectedElement.tagName === 'TABLE') {
			selectedElement.style.height = `${newHeight}px`;
		}
		
		// Update resize handles position
		const rect = selectedElement.getBoundingClientRect();
		const containerRect = contentEditableRef.current?.getBoundingClientRect();
		
		if (containerRect) {
			setResizeHandles({
				x: rect.left - containerRect.left,
				y: rect.top - containerRect.top,
				width: rect.width,
				height: rect.height
			});
		}
	}, [selectedElement, resizeHandles]);

	// Handle resize end
	const handleResizeEnd = useCallback(() => {
		document.removeEventListener('mousemove', handleResizeMove as any);
		document.removeEventListener('mouseup', handleResizeEnd);
		
		resizeStartRef.current = null;
		if (contentEditableRef.current) {
			sourceCodeRef.current = contentEditableRef.current.innerHTML;
			saveToHistory();
		}
	}, [handleResizeMove, saveToHistory]);

	// Show table picker
	const handleShowTablePicker = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		setTablePickerAnchor(e.currentTarget);
		setShowTablePicker(true);
	}, []);

	// Insert table with visual picker
	const handleInsertTableFromPicker = useCallback((rows: number, cols: number) => {
		// Focus the editor first
		if (contentEditableRef.current) {
			contentEditableRef.current.focus();
		}
		
		// Create table HTML
		let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
		tableHTML += '<tbody>';
		for (let i = 0; i < rows; i++) {
			tableHTML += '<tr>';
			for (let j = 0; j < cols; j++) {
				tableHTML += '<td style="border: 1px solid #ddd; padding: 8px; min-width: 50px;"></td>';
			}
			tableHTML += '</tr>';
		}
		tableHTML += '</tbody></table><p><br></p>';
		
		// Insert using execCommand
		document.execCommand('insertHTML', false, tableHTML);
		
		// Update refs and history
		if (contentEditableRef.current) {
			sourceCodeRef.current = contentEditableRef.current.innerHTML;
			saveToHistory();
		}
		
		// Close picker
		setShowTablePicker(false);
		setTablePickerAnchor(null);
	}, [saveToHistory]);

	// Create adapter object for ref
	const adapter = {
		getData: () => {
			// Always restore Handlebars syntax when getting data
			return prepareForSaving(sourceCodeRef.current, handlebarsMapRef.current);
		},
		setData: (newData: string) => {
			const { html: protectedHtml, map } = prepareForEditing(newData);
			handlebarsMapRef.current = map;
			sourceCodeRef.current = newData;
			if (contentEditableRef.current) {
				contentEditableRef.current.innerHTML = protectedHtml;
			}
		},
		model: {
			insertContent: (fragment: string) => {
				execCommand('insertHTML', fragment);
			}
		},
		data: {
			processor: {
				toView: (html: string) => html
			},
			toModel: (html: string) => html
		}
	};

	useImperativeHandle(ref, () => adapter, []);
	
	useEffect(() => {
		if (editorRefProp) {
			(editorRefProp as any).current = adapter;
		}
	}, [editorRefProp]);

	// Prevents focus loss when toolbar buttons are pressed, keeping the editor
	const toolbarCmd = (command: string, value?: string) => (e: React.MouseEvent) => {
		e.preventDefault();
		execCommand(command, value);
	};

	if (error) {
		return (
			<div className="editor-error-container">
				<div className="editor-error-message">Editor Error: {error.message}</div>
				<button onClick={() => setError(null)} className="editor-retry-button">
					Retry
				</button>
			</div>
		);
	}

	return (
		<div className='custom-editor-container'>
			{/* Menu Bar */}
			<Box sx={{ 
				display: 'flex', 
				alignItems: 'center',
				gap: 2,
				px: 2,
				py: 0.5,
				borderBottom: '1px solid #ddd',
				bgcolor: '#f8f9fa',
				fontSize: '13px'
			}}>
				<span style={{ cursor: 'pointer', color: '#333' }}>Edit</span>
				<span style={{ cursor: 'pointer', color: '#333' }}>View</span>
				<span style={{ cursor: 'pointer', color: '#333' }}>Insert</span>
				<span style={{ cursor: 'pointer', color: '#333' }}>Format</span>
				<span style={{ cursor: 'pointer', color: '#333' }}>Help</span>
			</Box>

			{/* Toolbar */}
			<Box sx={{ 
				display: 'flex', 
				flexWrap: 'wrap', 
				gap: 0.5, 
				p: 1, 
				borderBottom: '1px solid #ddd',
				bgcolor: '#f5f5f5',
				alignItems: 'center'
			}}>
				{/* Source Button - icon + label style matching the design */}
				<Tooltip title={showSourceMode ? 'Switch to Visual' : 'Switch to Source'}>
					<Box
						onClick={handleToggleSource}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
							px: 1,
							py: 0.5,
							cursor: 'pointer',
							borderRadius: 1,
							border: '1px solid',
							borderColor: showSourceMode ? 'primary.main' : 'transparent',
							bgcolor: showSourceMode ? 'primary.50' : 'transparent',
							color: showSourceMode ? 'primary.main' : 'text.secondary',
							'&:hover': {
								bgcolor: showSourceMode ? 'primary.100' : 'grey.200',
							},
							transition: 'all 0.15s ease',
							userSelect: 'none',
						}}
					>
						{/* Code-file-with-edit icon, matching the screenshot */}
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
							<polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
							<path d="M9 13L7 15L9 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
							<path d="M15 13L17 15L15 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
							<line x1="13" y1="12" x2="11" y2="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
						</svg>
						<span style={{ fontSize: '13px', fontWeight: 500 }}>Source</span>
					</Box>
				</Tooltip>

				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
				
				{/* Undo/Redo */}
				<Tooltip title="Undo">
					<IconButton size="small" onClick={handleUndo} disabled={historyIndex === 0}>
						<Undo fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Redo">
					<IconButton size="small" onClick={handleRedo} disabled={historyIndex === history.length - 1}>
						<Redo fontSize="small" />
					</IconButton>
				</Tooltip>
				
				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
				
				{/* Heading Format */}
				<Select
					value={headingFormat}
					onChange={handleHeadingFormatChange}
					size="small"
					sx={{ minWidth: 100, height: 28, fontSize: '12px' }}
				>
					{HEADING_FORMATS.map(format => (
						<MenuItem key={format.value} value={format.value}>
							{format.label}
						</MenuItem>
					))}
				</Select>
				
				{/* Font Family */}
				<Select
					value={fontFamily}
					onChange={handleFontFamilyChange}
					size="small"
					sx={{ minWidth: 120, height: 28, fontSize: '12px' }}
				>
					{FONT_FAMILIES.map(font => (
						<MenuItem key={font} value={font} style={{ fontFamily: font }}>
							{font}
						</MenuItem>
					))}
				</Select>
				
				{/* Font Size */}
				<Select
					value={fontSize}
					onChange={handleFontSizeChange}
					size="small"
					sx={{ minWidth: 70, height: 28, fontSize: '12px' }}
				>
					{FONT_SIZES.map(size => (
						<MenuItem key={size} value={size}>{size}</MenuItem>
					))}
				</Select>
				
				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
				
				{/* Text Formatting */}
				<Tooltip title="Bold">
					<IconButton size="small" onMouseDown={toolbarCmd('bold')}>
						<FormatBold fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Italic">
					<IconButton size="small" onMouseDown={toolbarCmd('italic')}>
						<FormatItalic fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Underline">
					<IconButton size="small" onMouseDown={toolbarCmd('underline')}>
						<FormatUnderlined fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Strikethrough">
					<IconButton size="small" onMouseDown={toolbarCmd('strikeThrough')}>
						<FormatStrikethrough fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Subscript">
					<IconButton size="small" onMouseDown={toolbarCmd('subscript')}>
						<Subscript fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Superscript">
					<IconButton size="small" onMouseDown={toolbarCmd('superscript')}>
						<Superscript fontSize="small" />
					</IconButton>
				</Tooltip>
				
				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
				
				{/* Colors */}
				<Tooltip title="Text Color">
					<IconButton
						size="small"
						onMouseDown={(e) => {
							e.preventDefault();
							setColorPickerState({ anchor: e.currentTarget, type: 'text' });
						}}
						style={{ width: 32, height: 28, border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white' }}
					>
						<span style={{ fontSize: '16px', fontWeight: 'bold', color: '#000' }}>A</span>
					</IconButton>
				</Tooltip>
				<Tooltip title="Background Color">
					<IconButton
						size="small"
						onMouseDown={(e) => {
							e.preventDefault();
							setColorPickerState({ anchor: e.currentTarget, type: 'bg' });
						}}
						style={{ width: 32, height: 28, border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fffde7' }}
					>
						<span style={{ fontSize: '16px', fontWeight: 'bold', color: '#000' }}>A</span>
					</IconButton>
				</Tooltip>
				
				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
				
				{/* Alignment */}
				<Tooltip title="Align Left">
					<IconButton size="small" onMouseDown={toolbarCmd('justifyLeft')}>
						<FormatAlignLeft fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Align Center">
					<IconButton size="small" onMouseDown={toolbarCmd('justifyCenter')}>
						<FormatAlignCenter fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Align Right">
					<IconButton size="small" onMouseDown={toolbarCmd('justifyRight')}>
						<FormatAlignRight fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Justify">
					<IconButton size="small" onMouseDown={toolbarCmd('justifyFull')}>
						<FormatAlignJustify fontSize="small" />
					</IconButton>
				</Tooltip>
				
				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
				
				{/* Lists */}
				<Tooltip title="Bullet List">
					<IconButton size="small" onMouseDown={toolbarCmd('insertUnorderedList')}>
						<FormatListBulleted fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Numbered List">
					<IconButton size="small" onMouseDown={toolbarCmd('insertOrderedList')}>
						<FormatListNumbered fontSize="small" />
					</IconButton>
				</Tooltip>
				
				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
				
				{/* Insert */}
				<Tooltip title="Insert Link">
					<IconButton size="small" onClick={handleInsertLink}>
						<LinkIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Insert Image">
					<IconButton size="small" onClick={() => fileInputRef.current?.click()}>
						<ImageIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Insert Table">
					<IconButton size="small" onClick={handleShowTablePicker}>
						<TableChart fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Code">
					<IconButton size="small" onMouseDown={toolbarCmd('formatBlock', '<pre>')}>
						<Code fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
			
			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>
			
			{/* Hidden HTML file input */}
			<input
				ref={htmlFileInputRef}
				type="file"
				accept=".html,.htm,text/html"
				style={{ display: 'none' }}
				onChange={handleHtmlFileUpload}
			/>
			
			{/* Hidden file input for replace image */}
			<input
				ref={replaceImageInputRef}
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				onChange={handleReplaceImageChange}
			/>
			
			{/* Content Area */}
			{showSourceMode ? (
				<textarea
					value={sourceCode}
					onChange={handleSourceCodeChange}
					className="custom-editor-source"
					style={{
						width: '100%',
						minHeight: '300px',
						padding: '16px',
						border: 'none',
						outline: 'none',
						fontFamily: 'Courier New, monospace',
						fontSize: '13px',
						lineHeight: '1.5',
						resize: 'vertical'
					}}
				/>
			) : (
				<div style={{ position: 'relative' }}>
					<div
						ref={contentEditableRef}
						contentEditable
						className="editor-content"
						onInput={handleInput}
						onPaste={handlePaste}
						onClick={handleContentClick}
						onMouseUp={handleTextSelection}
						onKeyUp={handleTextSelection}
						style={{
							minHeight: '300px',
							padding: '16px',
							border: '1px solid #ddd',
							borderTop: 'none',
							outline: 'none',
							fontFamily: 'inherit',
							fontSize: 'inherit',
							lineHeight: '1.6',
							backgroundColor: 'white'
						}}
						{...rest}
					/>
					
					{/* Resize Handles */}
					{selectedElement && resizeHandles && (
						<div
							style={{
								position: 'absolute',
								left: resizeHandles.x,
								top: resizeHandles.y,
								width: resizeHandles.width,
								height: resizeHandles.height,
								border: '2px solid #1976d2',
								pointerEvents: 'none',
								zIndex: 1000
							}}
						>
							{/* Toolbar for selected element */}
							<Box
								sx={{
									position: 'absolute',
									top: -36,
									left: 0,
									display: 'flex',
									gap: 0.5,
									bgcolor: 'white',
									border: '1px solid #ddd',
									borderRadius: 1,
									p: 0.25,
									boxShadow: 2,
									pointerEvents: 'auto'
								}}
							>
								{selectedElement.tagName === 'IMG' && (
									<Tooltip title="Replace Image">
										<IconButton
											size="small"
											onClick={() => replaceImageInputRef.current?.click()}
											sx={{ width: 22, height: 22 }}
										>
											<SwapHoriz sx={{ fontSize: 14 }} />
										</IconButton>
									</Tooltip>
								)}
								{selectedElement.tagName === 'TABLE' && (
									<>
										<Tooltip title="Add Row">
											<IconButton
												size="small"
												onClick={() => {
													const tbody = selectedElement.querySelector('tbody');
													if (tbody) {
														const colCount = selectedElement.querySelectorAll('tr:first-child td, tr:first-child th').length || 1;
														const tr = document.createElement('tr');
														for (let i = 0; i < colCount; i++) {
															const td = document.createElement('td');
															td.style.cssText = 'border: 1px solid #ddd; padding: 8px; min-width: 50px;';
															tr.appendChild(td);
														}
														tbody.appendChild(tr);
														if (contentEditableRef.current) sourceCodeRef.current = contentEditableRef.current.innerHTML;
													}
												}}
												sx={{ width: 22, height: 22 }}
											>
												<AddCircleOutline sx={{ fontSize: 14 }} />
											</IconButton>
										</Tooltip>
										<Tooltip title="Delete Row">
											<IconButton
												size="small"
												onClick={() => {
													const rows = selectedElement.querySelectorAll('tbody tr');
													if (rows.length > 1) {
														rows[rows.length - 1].remove();
														if (contentEditableRef.current) sourceCodeRef.current = contentEditableRef.current.innerHTML;
													}
												}}
												sx={{ width: 22, height: 22 }}
											>
												<RemoveCircleOutline sx={{ fontSize: 14 }} />
											</IconButton>
										</Tooltip>
										<Tooltip title="Add Column">
											<IconButton
												size="small"
												onClick={() => {
													selectedElement.querySelectorAll('tr').forEach(tr => {
														const isHeader = tr.closest('thead') !== null;
														const cell = document.createElement(isHeader ? 'th' : 'td');
														cell.style.cssText = 'border: 1px solid #ddd; padding: 8px; min-width: 50px;';
														tr.appendChild(cell);
													});
													if (contentEditableRef.current) sourceCodeRef.current = contentEditableRef.current.innerHTML;
												}}
												sx={{ width: 22, height: 22 }}
											>
												<AddCircleOutline sx={{ fontSize: 14, transform: 'rotate(90deg)' }} />
											</IconButton>
										</Tooltip>
										<Tooltip title="Delete Column">
											<IconButton
												size="small"
												onClick={() => {
													const allRows = selectedElement.querySelectorAll('tr');
													const colCount = allRows[0]?.children.length || 0;
													if (colCount > 1) {
														allRows.forEach(tr => {
															if (tr.lastElementChild) tr.removeChild(tr.lastElementChild);
														});
														if (contentEditableRef.current) sourceCodeRef.current = contentEditableRef.current.innerHTML;
													}
												}}
												sx={{ width: 22, height: 22 }}
											>
												<RemoveCircleOutline sx={{ fontSize: 14, transform: 'rotate(90deg)' }} />
											</IconButton>
										</Tooltip>
									</>
								)}
								<Tooltip title="Delete">
									<IconButton
										size="small"
										onClick={handleDeleteSelected}
										sx={{
											width: 22,
											height: 22,
											color: '#f44336',
											'&:hover': { bgcolor: '#ffebee' }
										}}
									>
										<Close sx={{ fontSize: 14 }} />
									</IconButton>
								</Tooltip>
							</Box>
							
							{/* Corner and edge handles */}
							{['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map(handle => (
								<div
									key={handle}
									onMouseDown={(e) => handleResizeStart(e, handle)}
									style={{
										position: 'absolute',
										width: 8,
										height: 8,
										backgroundColor: '#1976d2',
										border: '1px solid white',
										pointerEvents: 'auto',
										cursor: `${handle}-resize`,
										...(handle === 'nw' && { top: -4, left: -4 }),
										...(handle === 'n' && { top: -4, left: '50%', transform: 'translateX(-50%)' }),
										...(handle === 'ne' && { top: -4, right: -4 }),
										...(handle === 'e' && { top: '50%', right: -4, transform: 'translateY(-50%)' }),
										...(handle === 'se' && { bottom: -4, right: -4 }),
										...(handle === 's' && { bottom: -4, left: '50%', transform: 'translateX(-50%)' }),
										...(handle === 'sw' && { bottom: -4, left: -4 }),
										...(handle === 'w' && { top: '50%', left: -4, transform: 'translateY(-50%)' })
									}}
								/>
							))}
						</div>
					)}
					
					{/* Text Selection Toolbar */}
					{showTextToolbar && textSelectionAnchor && (
						<Box
							sx={{
								position: 'fixed',
								left: textSelectionAnchor.x,
								top: textSelectionAnchor.y,
								transform: 'translate(-50%, -100%)',
								display: 'flex',
								gap: 0.5,
								bgcolor: '#333',
								color: 'white',
								borderRadius: 1,
								p: 0.5,
								boxShadow: 3,
								zIndex: 10000
							}}
						>
							<Tooltip title="Bold">
								<IconButton
									size="small"
									onMouseDown={toolbarCmd('bold')}
									sx={{ color: 'white', width: 28, height: 28 }}
								>
									<FormatBold fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Italic">
								<IconButton
									size="small"
									onMouseDown={toolbarCmd('italic')}
									sx={{ color: 'white', width: 28, height: 28 }}
								>
									<FormatItalic fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Underline">
								<IconButton
									size="small"
									onMouseDown={toolbarCmd('underline')}
									sx={{ color: 'white', width: 28, height: 28 }}
								>
									<FormatUnderlined fontSize="small" />
								</IconButton>
							</Tooltip>
							<Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />
							<Tooltip title="Text Color">
								<IconButton
									size="small"
									sx={{ color: 'white', width: 28, height: 28 }}
									onMouseDown={(e) => {
										e.preventDefault();
										setColorPickerState({ anchor: e.currentTarget, type: 'text' });
									}}
								>
									<span style={{ fontSize: '14px', fontWeight: 'bold' }}>A</span>
								</IconButton>
							</Tooltip>
							<Tooltip title="Font Size">
								<Select
									size="small"
									defaultValue="14px"
									onChange={handleFontSizeChange}
									sx={{ 
										color: 'white',
										height: 28,
										minWidth: 70,
										'& .MuiOutlinedInput-notchedOutline': { border: 'none' },
										'& .MuiSvgIcon-root': { color: 'white' }
									}}
								>
									{FONT_SIZES.map(size => (
										<MenuItem key={size} value={size}>{size}</MenuItem>
									))}
								</Select>
							</Tooltip>
						</Box>
					)}
				</div>
			)}
			
			{/* Color Picker Popover */}
			{colorPickerState && (
				<Popover
					open
					anchorEl={colorPickerState.anchor}
					onClose={() => setColorPickerState(null)}
					disableAutoFocus
					disableEnforceFocus
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				>
					<Box sx={{ p: 1 }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 20px)', gap: '3px' }}>
							{PRESET_COLORS.map((color) => (
								<Box
									key={color}
									onMouseDown={(e) => {
										e.preventDefault();
										if (colorPickerState.type === 'text') {
											execCommand('foreColor', color);
										} else {
											execCommand('hiliteColor', color);
										}
										setColorPickerState(null);
									}}
									sx={{
										width: 20,
										height: 20,
										bgcolor: color,
										cursor: 'pointer',
										border: '1px solid rgba(0,0,0,0.15)',
										borderRadius: '2px',
										'&:hover': { transform: 'scale(1.3)', zIndex: 1, position: 'relative' },
									}}
								/>
							))}
						</Box>
					</Box>
				</Popover>
			)}

			{/* Table Picker Popover */}
			{showTablePicker && tablePickerAnchor && (
				<>
					<Box
						onClick={() => {
							setShowTablePicker(false);
							setTablePickerAnchor(null);
						}}
						sx={{
							position: 'fixed',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 9998
						}}
					/>
					<TablePicker />
				</>
			)}
		</div>
	);
});

CustomEditor.displayName = 'CustomEditor';
export default CustomEditor;